import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, LegalDocumentItem } from '../services/api';
import { C } from '../components/UI';
import { useBranding } from '../context/BrandingContext';

const EFFECTIVE_DATE = 'July 1, 2025';

export default function PrivacyPolicy() {
    const navigate = useNavigate();
    const branding = useBranding();
    const navText = branding.navTextColor ?? '#ffffff';
    // TREK-129: sourced from branding, not hardcoded — was 'BoxRunner, LLC' / 'privacy@boxrunner.com'.
    const COMPANY = branding.appName || 'BoxRunner';
    const EMAIL = branding.supportEmail || 'privacy@boxrunner.com';

    // Admin-edited copy (Admin Dashboard > Disclosures > Site Legal Pages) takes over when
    // present; the sections below remain as the fallback for a tenant that hasn't customized it.
    const [doc, setDoc] = useState<LegalDocumentItem | null>(null);
    useEffect(() => { api.getLegalDocument('PrivacyPolicy').then(setDoc).catch(() => {}); }, []);

    return (
        <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif", color: C.text }}>
            {/* Header */}
            <div style={{ background: branding.primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56 }}>
                {branding.logoUrl
                    ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 32, maxWidth: 120, objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate(-1)} />
                    : <span style={{ fontSize: 18, fontWeight: 700, color: navText, letterSpacing: -0.5, cursor: 'pointer' }} onClick={() => navigate(-1)}>
                        {branding.appName}
                      </span>
                }
                <span style={{ color: navText, fontSize: 13, opacity: 0.7 }}>· Privacy Policy</span>
                <button
                    onClick={() => navigate(-1)}
                    style={{ marginLeft: 'auto', background: 'transparent', border: `1px solid ${navText}`, color: navText, borderRadius: 6, padding: '4px 12px', fontSize: 13, cursor: 'pointer', opacity: 0.8 }}
                >
                    ← Back
                </button>
            </div>

            {/* Content */}
            <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px 80px' }}>
                {doc ? (
                    <>
                        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>{doc.title}</h1>
                        <p style={{ color: C.textSub, fontSize: 14, marginBottom: 40 }}>Updated {new Date(doc.updatedAt).toLocaleDateString()}</p>
                        <div style={{ fontSize: 14, lineHeight: 1.75, color: C.text, whiteSpace: 'pre-wrap' }}>{doc.body}</div>
                    </>
                ) : (
                <>
                <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>Privacy Policy</h1>
                <p style={{ color: C.textSub, fontSize: 14, marginBottom: 40 }}>Effective {EFFECTIVE_DATE}</p>

                <Section title="1. Who We Are">
                    <p>{COMPANY} ("BoxRunner," "we," "us," or "our") operates the BoxRunner platform, which connects customers with independent junk-removal contractors. This Privacy Policy explains how we collect, use, share, and protect your information when you use our website, mobile app, or related services (collectively, the "Service").</p>
                    <p>If you have questions, contact us at <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a>.</p>
                </Section>

                <Section title="2. Information We Collect">
                    <SubHeading>2.1 Information You Provide</SubHeading>
                    <ul>
                        <li><strong>Account information</strong> — name, email address, phone number, and password when you register.</li>
                        <li><strong>Booking details</strong> — pickup address, service type, estimated item counts, and scheduling preferences.</li>
                        <li><strong>Photos</strong> — images of items you upload to help estimate your job. These are stored securely in Azure Blob Storage.</li>
                        <li><strong>Payment information</strong> — credit/debit card details entered at checkout. Card numbers are processed by Stripe and are never stored on our servers.</li>
                        <li><strong>Communications</strong> — messages you send through our in-app chat.</li>
                    </ul>

                    <SubHeading>2.2 Information We Collect Automatically</SubHeading>
                    <ul>
                        <li><strong>Usage data</strong> — pages visited, features used, and interactions with the Service.</li>
                        <li><strong>Device information</strong> — device type, operating system, app version, and unique device identifiers.</li>
                        <li><strong>Location</strong> — approximate location derived from your IP address, and (with your permission) precise GPS location when you use the app to track a contractor en route.</li>
                        <li><strong>Log data</strong> — IP address, browser type, referring URLs, and timestamps.</li>
                    </ul>
                </Section>

                <Section title="3. How We Use Your Information">
                    <ul>
                        <li>Provide, operate, and improve the Service, including matching you with available contractors.</li>
                        <li>Process payments and send booking confirmations, receipts, and service updates.</li>
                        <li>Use job photos to generate AI-assisted item estimates and display them to the assigned contractor.</li>
                        <li>Enable real-time contractor tracking during active jobs.</li>
                        <li>Respond to your support requests and communications.</li>
                        <li>Send transactional emails (booking confirmations, status updates). We do not send marketing emails without your explicit consent.</li>
                        <li>Detect fraud, enforce our Terms of Service, and comply with legal obligations.</li>
                        <li>Analyze aggregate usage patterns to improve features and performance.</li>
                    </ul>
                </Section>

                <Section title="4. How We Share Your Information">
                    <SubHeading>4.1 With Contractors</SubHeading>
                    <p>When you book a job, we share your first name, pickup address, service details, and job photo with the contractor assigned to your job. We do not share your full contact details until the job is confirmed.</p>

                    <SubHeading>4.2 With Service Providers</SubHeading>
                    <p>We work with trusted third-party providers who process data on our behalf:</p>
                    <ul>
                        <li><strong>Stripe</strong> — payment processing</li>
                        <li><strong>Microsoft Azure</strong> — cloud hosting and file storage</li>
                        <li><strong>SendGrid</strong> — transactional email delivery</li>
                        <li><strong>OpenAI</strong> — AI-assisted item estimation from photos</li>
                        <li><strong>Firebase / Google</strong> — push notifications</li>
                    </ul>
                    <p>These providers are contractually obligated to use your data only to perform services for us and may not use it for their own purposes.</p>

                    <SubHeading>4.3 Legal Requirements</SubHeading>
                    <p>We may disclose your information if required by law, court order, or to protect the rights, safety, or property of BoxRunner, our users, or the public.</p>

                    <SubHeading>4.4 Business Transfers</SubHeading>
                    <p>If BoxRunner is acquired or merges with another company, your information may be transferred as part of that transaction. We will notify you before your data becomes subject to a different privacy policy.</p>

                    <SubHeading>4.5 No Sale of Personal Data</SubHeading>
                    <p>We do not sell, rent, or trade your personal information to third parties for their own marketing purposes.</p>
                </Section>

                <Section title="5. Photos and AI Processing">
                    <p>Photos you upload are stored securely in Azure Blob Storage and may be processed by OpenAI to estimate the volume and type of items for pickup. Photos are shared with your assigned contractor to help them prepare for the job. We retain job photos for up to 12 months after job completion, after which they are deleted.</p>
                </Section>

                <Section title="6. Data Retention">
                    <p>We retain your account information for as long as your account is active or as needed to provide the Service. You may delete your account and associated data at any time — in the app or on the web, go to Account Settings and select "Delete account," then confirm with your password. This immediately cancels any active subscription and permanently removes your profile, saved addresses, and payment methods. Job history and photos are retained for up to 12 months after job completion for support and fraud-prevention purposes, then deleted; we may also retain certain records as required by law (e.g., tax and payment records). If you're unable to access the app, you can request deletion the same way by contacting <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a>.</p>
                </Section>

                <Section title="7. Security">
                    <p>We use industry-standard security measures, including TLS encryption in transit and Azure-managed encryption at rest, to protect your information. No method of transmission over the internet is 100% secure, however, and we cannot guarantee absolute security.</p>
                </Section>

                <Section title="8. Children's Privacy">
                    <p>The Service is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.</p>
                </Section>

                <Section title="9. Your Rights and Choices">
                    <ul>
                        <li><strong>Access and correction</strong> — you may view and update your account information at any time through the app.</li>
                        <li><strong>Deletion</strong> — delete your account yourself at any time from Account Settings ("Delete account"), or by emailing <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a> if you can't access the app. See Section 6 for what's removed immediately versus retained.</li>
                        <li><strong>Location</strong> — you may revoke GPS location permission through your device settings at any time. This will disable real-time contractor tracking.</li>
                        <li><strong>Push notifications</strong> — you may disable push notifications through your device settings.</li>
                    </ul>
                    <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect and to request its deletion. Contact us at <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a> to exercise these rights.</p>
                </Section>

                <Section title="10. Third-Party Links">
                    <p>The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties and encourage you to review their privacy policies.</p>
                </Section>

                <Section title="11. Changes to This Policy">
                    <p>We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or by displaying a notice in the app before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the updated policy.</p>
                </Section>

                <Section title="12. Contact Us">
                    <p>If you have questions about this Privacy Policy or your data, please contact us:</p>
                    <p style={{ marginTop: 8 }}>
                        <strong>{COMPANY}</strong><br />
                        Email: <a href={`mailto:${EMAIL}`} style={{ color: C.greenMid }}>{EMAIL}</a>
                    </p>
                </Section>
                </>
                )}
            </div>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: C.green, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>{title}</h2>
            <div style={{ fontSize: 14, lineHeight: 1.75, color: C.text, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {children}
            </div>
        </section>
    );
}

function SubHeading({ children }: { children: React.ReactNode }) {
    return <p style={{ fontWeight: 600, marginBottom: -4, color: C.text }}>{children}</p>;
}
